import React, { useState, useMemo } from 'react'
import {
  Box,
  Button,
  SpaceBetween,
  StatusIndicator,
  Badge,
  Alert,
  TreeView,
  Icon,
  Link
} from '@cloudscape-design/components'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'
import { RightSidebar, FilterButton } from '../../../shared/components'
import type { Id } from '../../../../convex/_generated/dataModel'

interface SelectedAsset {
  _id: string
  name: string
  type: 'factory' | 'production_line' | 'equipment_component'
  status: string
  external_asset_id: string
  sitewise_asset_id?: string
}

interface AssetHierarchyProps {
  onAssetSelect: (asset: SelectedAsset | null) => void
  selectedAsset: SelectedAsset | null
}

type AssetItem = {
  id: string
  type: 'factory' | 'production_line' | 'equipment_component'
  name: string
  status: string
  parentId?: string
  data?: any
  equipmentCount?: number
  external_asset_id?: string
}

export default function AssetHierarchy({ onAssetSelect, selectedAsset }: AssetHierarchyProps) {
  const { sessionId } = useAmplifyAuth()
  const [hierarchyExpanded, setHierarchyExpanded] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [assetTypeFilter, setAssetTypeFilter] = useState<any>(null)
  const [statusFilter, setStatusFilter] = useState<any>(null)

  // Real Convex queries for synced data
  const factories = useQuery(
    sessionId ? api.components.assets.index.getFactories : "skip",
    sessionId ? { sessionId } : "skip"
  )
  const productionLines = useQuery(
    sessionId ? api.components.assets.index.getProductionLines : "skip",
    sessionId ? { sessionId } : "skip"
  )
  const equipment = useQuery(
    sessionId ? api.components.assets.index.getEquipmentComponents : "skip",
    sessionId ? { sessionId } : "skip"
  )

  // Build hierarchical tree data structure from synced assets
  const allTreeItems: AssetItem[] = useMemo(() => {
    const items: AssetItem[] = []
    
    // Add Factories with enhanced info
    if (factories) {
      factories.forEach(factory => {
        const factoryLines = productionLines?.filter(line => line.factory_id === factory._id) || []
        const factoryEquipment = equipment?.filter(eq => 
          factoryLines.some(line => line._id === eq.production_line_id)
        ) || []
        
        items.push({
          id: factory._id,
          type: 'factory',
          name: factory.name,
          status: factory.status,
          equipmentCount: factoryEquipment.length,
          external_asset_id: factory.external_asset_id,
          data: factory
        })
      })
    }
    
    // Add Production Lines with equipment counts
    if (productionLines) {
      productionLines.forEach(line => {
        const lineEquipment = equipment?.filter(eq => eq.production_line_id === line._id) || []
        
        items.push({
          id: line._id,
          type: 'production_line',
          name: line.name,
          status: line.status,
          parentId: line.factory_id,
          equipmentCount: lineEquipment.length,
          external_asset_id: line.external_asset_id,
          data: line
        })
      })
    }
    
    // Add Equipment Components
    if (equipment) {
      equipment.forEach(eq => {
        items.push({
          id: eq._id,
          type: 'equipment_component',
          name: eq.name,
          status: eq.component_type,
          parentId: eq.production_line_id,
          external_asset_id: eq.external_asset_id,
          data: eq
        })
      })
    }
    
    return items
  }, [factories, productionLines, equipment])

  // Apply filters to tree items
  const filteredTreeItems = useMemo(() => {
    return allTreeItems.filter(item => {
      // Text search filter
      const matchesSearch = !searchText || 
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.external_asset_id?.toLowerCase().includes(searchText.toLowerCase())
      
      // Asset type filter
      const matchesType = !assetTypeFilter || assetTypeFilter.value === 'all' || item.type === assetTypeFilter.value
      
      // Status filter
      const matchesStatus = !statusFilter || statusFilter.value === 'all' || item.status === statusFilter.value
      
      return matchesSearch && matchesType && matchesStatus
    })
  }, [allTreeItems, searchText, assetTypeFilter, statusFilter])

  // Asset filter configurations
  const assetFilters = useMemo(() => [
    {
      id: 'assetType',
      type: 'buttongroup' as const,
      label: 'Asset Type',
      options: [
        { 
          label: 'Factories', 
          value: 'factory', 
          count: allTreeItems.filter(item => item.type === 'factory').length 
        },
        { 
          label: 'Lines', 
          value: 'production_line', 
          count: allTreeItems.filter(item => item.type === 'production_line').length 
        },
        { 
          label: 'Equipment', 
          value: 'equipment_component', 
          count: allTreeItems.filter(item => item.type === 'equipment_component').length 
        }
      ],
      selectedOption: assetTypeFilter,
      onChange: setAssetTypeFilter,
      showCounts: true
    },
    {
      id: 'status',
      type: 'select' as const,
      label: 'Status',
      options: [
        { 
          label: 'Operational', 
          value: 'operational', 
          count: allTreeItems.filter(item => item.status === 'operational' || item.status === 'running').length 
        },
        { 
          label: 'Idle', 
          value: 'idle', 
          count: allTreeItems.filter(item => item.status === 'idle').length 
        },
        { 
          label: 'Shutdown', 
          value: 'shutdown', 
          count: allTreeItems.filter(item => item.status === 'shutdown').length 
        }
      ],
      selectedOption: statusFilter,
      onChange: setStatusFilter,
      showCounts: true
    }
  ], [allTreeItems, assetTypeFilter, statusFilter])

  // Clear all filters
  const clearAllFilters = () => {
    setSearchText('')
    setAssetTypeFilter(null)
    setStatusFilter(null)
  }

  // Get root items (factories) from filtered results
  const rootItems = filteredTreeItems.filter(item => !item.parentId)

  // Enhanced TreeView helper functions
  const getItemId = (item: AssetItem) => item.id
  
  const getItemChildren = (item: AssetItem) => {
    return filteredTreeItems.filter(child => child.parentId === item.id)
  }
  
  const renderItem = (item: AssetItem) => {
    const icons = {
      factory: <Icon name="external" />,
      production_line: <Icon name="settings" />,
      equipment_component: <Icon name="hardware" />
    }

    const isSelected = selectedAsset?._id === item.id
    
    return {
      content: (
        <div 
          style={{ 
            cursor: 'pointer',
            padding: '12px 8px',
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
            borderRadius: '8px',
            border: isSelected ? '2px solid #1976d2' : '2px solid transparent',
            transition: 'all 0.2s ease'
          }}
          onClick={() => handleAssetItemSelection(item)}
        >
          <span style={{ 
            minWidth: '20px',
            display: 'flex',
            justifyContent: 'center',
            color: item.type === 'factory' ? '#1976d2' : item.type === 'production_line' ? '#388e3c' : '#616161'
          }}>
            {icons[item.type]}
          </span>
          
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: item.type === 'factory' ? '600' : '500',
              fontSize: item.type === 'factory' ? '16px' : '14px',
              color: item.type === 'factory' ? '#1976d2' : 'inherit',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {item.name}
            </div>
            
            {/* Show equipment count only for production lines */}
            {item.type === 'production_line' && item.equipmentCount !== undefined && item.equipmentCount > 0 && (
              <div style={{ 
                fontSize: '12px', 
                color: '#666',
                marginTop: '2px'
              }}>
                {item.equipmentCount} equipment component{item.equipmentCount !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Simple status indicator */}
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: 
              item.status === 'operational' || item.status === 'running' ? '#4caf50' :
              item.status === 'idle' ? '#ff9800' :
              item.status === 'shutdown' ? '#f44336' : '#9e9e9e'
          }} />
        </div>
      )
    }
  }

  // Handle asset selection from TreeView
  const handleAssetItemSelection = (item: AssetItem) => {
    const selectedAsset: SelectedAsset = {
      _id: item.id,
      name: item.name,
      type: item.type,
      status: item.status,
      external_asset_id: item.external_asset_id || '',
      sitewise_asset_id: item.data?.sitewise_asset_id
    }
    onAssetSelect(selectedAsset)
  }

  // Loading and empty states
  if (!sessionId) {
    return (
      <Alert type="warning">
        Please log in to view assets.
      </Alert>
    )
  }

  if (factories === undefined || productionLines === undefined || equipment === undefined) {
    return (
      <Box textAlign="center" color="inherit">
        <StatusIndicator type="loading">Loading asset hierarchy...</StatusIndicator>
      </Box>
    )
  }

  return (
    <div style={{ 
      height: 'calc(100vh - 200px)',
      overflow: 'hidden'
    }}>
      <RightSidebar
        isExpanded={hierarchyExpanded}
        onToggle={() => setHierarchyExpanded(!hierarchyExpanded)}
        title="Asset Hierarchy"
        viewMode="tree"
        width={320}
        minWidth={280}
        collapsedItems={filteredTreeItems.map(item => ({
          id: item.id,
          name: item.name,
          type: item.type,
          status: item.status,
          onClick: () => handleAssetItemSelection(item)
        }))}
        emptyState={{
          icon: "📁",
          title: "No assets found",
          description: rootItems.length === 0 && !searchText && !assetTypeFilter && !statusFilter
            ? "Visit Asset Discovery to discover and sync your SiteWise assets"
            : "No assets match the current filters",
          action: rootItems.length === 0 && !searchText && !assetTypeFilter && !statusFilter
            ? <Link href="/assets/discover">Discover Assets</Link>
            : undefined
        }}
        headerActions={
          <FilterButton
            searchPlaceholder="Search assets..."
            searchValue={searchText}
            onSearchChange={setSearchText}
            filters={assetFilters}
            onClearAll={clearAllFilters}
            showClearAll={true}
            modalTitle="Filter Assets"
            actions={[
              {
                id: 'expand-all',
                label: 'Expand All',
                icon: 'expand',
                onClick: () => {
                  console.log('Expand all clicked')
                }
              }
            ]}
          />
        }
      >
        {rootItems.length > 0 && (
          <TreeView
            items={rootItems}
            renderItem={renderItem}
            getItemId={getItemId}
            getItemChildren={getItemChildren}
            onItemToggle={({ detail }) => {
              // Handle expand/collapse if needed
            }}
            ariaLabel="Asset hierarchy"
          />
        )}
      </RightSidebar>
    </div>
  )
}